import { useTranslations } from '@vocab/react';
import { Notice, Stack, Text, TextLinkButton } from 'braid-design-system';
import { useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { VirusScanStatus } from 'src/__generated__/graphql';
import {
  SelectionCriteriaFields,
  type SelectionCriteriaFieldsValues,
} from 'src/components/SelectionCriteriaFields/SelectionCriteriaFields';
import { FileAttachment } from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import { useVirusScan } from 'src/hooks/useVirusScan';
import DrawerDocument from 'src/pages/Review/components/DrawerDocument';

import translations from './.vocab';

interface PropsSelectionCriteriaItem {
  onChange: (values: Partial<SelectionCriteriaFieldsValues>) => void;
}

export function SelectionCriteriaItem({
  onChange,
}: PropsSelectionCriteriaItem) {
  const { t } = useTranslations(translations);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const rootForm = useFormContext<SelectionCriteriaFieldsValues>();
  const form = useForm<SelectionCriteriaFieldsValues>({});
  const { handleSubmit, reset, formState } = form;

  useVirusScan({
    fileUri: rootForm.watch('selectionCriteria-uri'),
    currentMethod: rootForm.watch('selectionCriteria-method'),
    onStatusChange: (virusScanStatus, method) => {
      const shouldCheck = method === FileAttachment.UPLOAD;
      if (!shouldCheck) {
        rootForm.clearErrors('selectionCriteria-virusScan');
      } else if (virusScanStatus === VirusScanStatus.HasVirus) {
        rootForm.setError('selectionCriteria-virusScan', {
          type: VirusScanStatus.HasVirus,
          message: t('error.virus.document', {
            Button: (text) => text,
          })?.toString(),
        });
      } else if (virusScanStatus === VirusScanStatus.Pending) {
        rootForm.setError('selectionCriteria-virusScan', {
          type: VirusScanStatus.Pending,
          message: t('error.virus.pending'),
        });
      } else {
        rootForm.clearErrors('selectionCriteria-virusScan');
      }
    },
  });

  const selectionCriteriaText = rootForm.watch('selectionCriteria-text');
  const selectionCriteriaFile = rootForm.watch('selectionCriteria-file');
  const selectionCriteriaMethod = rootForm.watch('selectionCriteria-method');

  let currentSelectionText = t('No selection criteria');
  let currentSelectionTestId = 'selection-criteria-dont';

  if (selectionCriteriaMethod === FileAttachment.CHANGE) {
    currentSelectionText = t('You wrote a selection criteria');
    currentSelectionTestId = 'selection-criteria-write';
  }

  if (selectionCriteriaMethod === FileAttachment.UPLOAD) {
    currentSelectionText = selectionCriteriaFile.name;
    currentSelectionTestId = 'selection-criteria-upload';
  }

  const closeForm = () => {
    setIsFormOpen(false);
    reset(rootForm.getValues());
  };

  const confirmEdit = (values: Partial<SelectionCriteriaFieldsValues>) => {
    onChange(values);
    setIsFormOpen(false);
  };

  const virusScanStatus =
    rootForm.formState.errors['selectionCriteria-virusScan']?.type;

  return (
    <Stack space="small">
      <DrawerDocument
        title={t('Selection criteria')}
        currentSelectionText={currentSelectionText}
        currentSelectionTestId={currentSelectionTestId}
        editButtonLabel={t('Edit selection criteria')}
        editButtonId="edit-selection-criteria"
        onSubmit={handleSubmit(confirmEdit)}
        onClose={closeForm}
        shouldConfirmBeforeClose={formState.isDirty}
        isFormOpen={isFormOpen}
        setIsFormOpen={setIsFormOpen}
      >
        <FormProvider {...form}>
          <SelectionCriteriaFields
            hideTitle
            defaultMethod={selectionCriteriaMethod}
            defaultValue={
              // eslint-disable-next-line no-nested-ternary
              selectionCriteriaMethod === FileAttachment.CHANGE
                ? selectionCriteriaText
                : selectionCriteriaMethod === FileAttachment.UPLOAD
                  ? selectionCriteriaFile
                  : undefined
            }
          />
        </FormProvider>
      </DrawerDocument>
      {virusScanStatus === VirusScanStatus.HasVirus && (
        <Notice tone="critical">
          <Text size="small">
            {t('error.virus.document', {
              Button: (text) => (
                <TextLinkButton onClick={() => setIsFormOpen(true)}>
                  {text}
                </TextLinkButton>
              ),
            })}
          </Text>
        </Notice>
      )}
      {virusScanStatus === VirusScanStatus.Pending && (
        <Notice tone="critical">
          <Text size="small">{t('error.virus.pending')}</Text>
        </Notice>
      )}
    </Stack>
  );
}
